import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SingleSelectComponent, ModalComponent } from '../primeng';
import { AuthApiService, FileApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';
import { Subject, takeUntil } from 'rxjs';
import { PdfViewerModule } from 'ng2-pdf-viewer';

@Component({
  selector: 'app-request-credits',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SingleSelectComponent,
    ModalComponent,
    PdfViewerModule,
  ],
  templateUrl: './request-credits.component.html',
  styleUrl: './request-credits.component.scss',
})
export class RequestCreditsComponent implements OnInit, OnDestroy {
  /**
   * When used inside Admin "View" modal, we pass the API's `additionalInfo` here.
   * In that mode, we should NOT call getUserInfo(), and we should disable all fields.
   */
  @Input() initialData: any | null = null;
  @Input() readOnly: boolean = false;
  /** When true, user already has a pending credit request; form submit is disabled. */
  @Input() hasPendingRequest: boolean = false;

  /** Emitted when a credit request is successfully submitted so the parent can refresh pending state and show the warning/withdraw UI. */
  @Output() requestSubmitted = new EventEmitter<void>();

  requestCreditsForm!: FormGroup;
  private organizationDataFromApi: any = null;
  private userDataFromApi: any = null;
  private destroy$ = new Subject<void>();
  hasNoOrganisation: boolean = false; // Flag to track if user has no organisation
  
  // PDF Viewer properties
  showPdfPreviewModal: boolean = false;
  currentPdfUrl: string | null = null;
  pdfLoading: boolean = false;
  uploadedResumeUrl: string | null = null; // Store the uploaded file URL from API
  
  expandedSections: { [key: string]: boolean } = {
    'applicant-info': true,
    'project-overview': false,
    'technical-info': false,
    'team-credentials': false
  };
  
  // Character counters
  get projectDescriptionLength(): number {
    const value = this.requestCreditsForm?.get('projectDescription')?.value || '';
    return value.length;
  }
  
  get computeJustificationLength(): number {
    const value = this.requestCreditsForm?.get('computeJustification')?.value || '';
    return value.length;
  }
  
  get briefJustificationLength(): number {
    const value = this.requestCreditsForm?.get('briefJustification')?.value || '';
    return value.length;
  }
  
  // Options for Request Credits form dropdowns
  projectSectorOptions = [
    { label: 'Agriculture & Farming', value: 'agriculture' },
    { label: 'Automotive/Mobility', value: 'automotive' },
    { label: 'Biotechnology', value: 'biotechnology' },
    { label: 'Climate & Sustainability', value: 'climate' },
    { label: 'Construction', value: 'construction' },
    { label: 'Consulting', value: 'consulting' },
    { label: 'E-commerce & Marketplaces', value: 'ecommerce' },
    { label: 'Education', value: 'education' },
    { label: 'Energy', value: 'energy' },
    { label: 'Finance', value: 'finance' },
    { label: 'Forestry', value: 'forestry' },
    { label: 'Healthcare', value: 'healthcare' },
    { label: 'Hospitality & Tourism', value: 'hospitality' },
    { label: 'Human Resources & Staffing', value: 'hr' },
    { label: 'Information Technology (IT)', value: 'it' },
    { label: 'Insurance', value: 'insurance' },
    { label: 'Investment & Asset Management', value: 'investment' },
    { label: 'Legal Services', value: 'legal' },
    { label: 'Logistics & Transportation', value: 'logistics' },
    { label: 'Manufacturing', value: 'manufacturing' },
    { label: 'Marketing & Advertising', value: 'marketing' },
    { label: 'Media & Entertainment', value: 'media' },
    { label: 'Mining & Metals', value: 'mining' },
    { label: 'MSME/Startup', value: 'msme' },
    { label: 'Non-Profit / NGO', value: 'nonprofit' },
    { label: 'Oil & Gas', value: 'oilgas' },
    { label: 'Public Enterprises', value: 'public-enterprises' },
    { label: 'Real Estate & Property Management', value: 'realestate' },
    { label: 'Retail & Consumer Goods', value: 'retail' },
    { label: 'Rural Development', value: 'rural' },
    { label: 'Sector Agnostic', value: 'agnostic' },
    { label: 'Semiconductor & Electronics', value: 'semiconductor' },
    { label: 'Shipping & Maritime', value: 'shipping' },
    { label: 'Sports & Recreation', value: 'sports' },
    { label: 'Telecommunications & Networking', value: 'telecom' },
    { label: 'Textiles & Apparel', value: 'textiles' },
    { label: 'Welfare', value: 'welfare' },
    { label: 'Other', value: 'other' }
  ];

  endUsersOptions = [
    { label: 'Public', value: 'public' },
    { label: 'Government', value: 'government' },
    { label: 'Enterprise', value: 'enterprise' },
    { label: 'Startups', value: 'startups' },
    { label: 'Academic/Research', value: 'academic' },
    { label: 'Non-profit/NGOs', value: 'nonprofit' },
    { label: 'Other', value: 'other' }
  ];

  projectStageOptions = [
    { label: 'Proof of Concept (PoC)', value: 'poc' },
    { label: 'Pilot', value: 'pilot' },
    { label: 'Minimum Viable Product (MVP)', value: 'mvp' },
    { label: 'Production', value: 'production' },
    { label: 'Scaling', value: 'scaling' }
  ];

  // Technical Information Options
  modelTypeOptions = [
    { label: 'Convolutional Neural Network (CNN)', value: 'cnn' },
    { label: 'Recurrent Neural Network (RNN)', value: 'rnn' },
    { label: 'Transformer', value: 'transformer' },
    { label: 'Generative Adversarial Network (GAN)', value: 'gan' },
    { label: 'Large Language Model (LLM)', value: 'llm' },
    { label: 'Computer Vision Model', value: 'cv' },
    { label: 'Natural Language Processing (NLP)', value: 'nlp' },
    { label: 'Reinforcement Learning', value: 'rl' },
    { label: 'Other', value: 'other' }
  ];

  trainingTypeOptions = [
    { label: 'Pre-trained', value: 'pretrained' },
    { label: 'New Training', value: 'new_training' }
  ];

  inputDataTypeOptions = [
    { label: 'Images', value: 'images' },
    { label: 'Text', value: 'text' },
    { label: 'Audio', value: 'audio' },
    { label: 'Video', value: 'video' },
    { label: 'Tabular Data', value: 'tabular' },
    { label: 'Time Series', value: 'timeseries' },
    { label: 'Multimodal', value: 'multimodal' },
    { label: 'Other', value: 'other' }
  ];

  dataVolumeOptions = [
    { label: '< 1 GB', value: 'lt_1gb' },
    { label: '1 GB - 10 GB', value: '1_10gb' },
    { label: '10 GB - 100 GB', value: '10_100gb' },
    { label: '100 GB - 1 TB', value: '100gb_1tb' },
    { label: '1 TB - 10 TB', value: '1_10tb' },
    { label: '> 10 TB', value: 'gt_10tb' }
  ];

  tgdexUsageOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' }
  ];

  // Team Credentials Options
  teamSizeOptions = [
    { label: '1-5 members', value: '1-5' },
    { label: '6-10 members', value: '6-10' },
    { label: '11-20 members', value: '11-20' },
    { label: '21-50 members', value: '21-50' },
    { label: '50+ members', value: '50+' }
  ];

  supportOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' }
  ];

  // File upload state
  selectedResumeFile: File | null = null;
  resumeFileName: string = '';
  private readonly organisationFields = [
    'organisationName',
    'organisationType',
    'organisationLocation',
    'organisationWebsiteUrl',
    'jobTitle',
    'businessEmail'
  ] as const;

  constructor(
    private fb: FormBuilder,
    private authApiService: AuthApiService,
    private toasterService: ToasterService,
    private fileApiService: FileApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initRequestCreditsForm();

    // View mode: populate from input and disable everything
    if (this.readOnly) {
      this.applyInitialDataToForm(this.initialData);
      this.disableAllFields();
      return;
    }

    // Normal mode: apply initialData first if present (e.g. pending request from parent),
    // so form is prepopulated even when initialData was bound before ngOnInit (ngOnChanges runs before ngOnInit so form wasn't ready).
    if (this.initialData) {
      this.applyInitialDataToForm(this.initialData);
    }

    // Prefill applicant/org from user API (won't overwrite project/technical/team fields)
    this.loadUserData();
    
    // Check if resumeFile has a value (e.g., after form reload or initial data)
    setTimeout(() => {
      this.checkAndSetResumeFileName();
    }, 100);
  }
  
  /**
   * Check if resumeFile has a value and set resumeFileName accordingly
   */
  private checkAndSetResumeFileName(): void {
    if (!this.requestCreditsForm) return;
    
    const resumeFileValue = this.requestCreditsForm.get('resumeFile')?.value;
    if (resumeFileValue && resumeFileValue.trim() !== '' && !this.resumeFileName) {
      // Extract file name from the key
      const fileName = this.extractFileNameFromKey(resumeFileValue);
      if (fileName) {
        this.resumeFileName = fileName;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['initialData'] || !this.requestCreditsForm) return;

    const prev = changes['initialData'].previousValue;
    const curr = changes['initialData'].currentValue;

    if (this.readOnly) {
      this.applyInitialDataToForm(this.initialData);
      this.disableAllFields();
      return;
    }

    // Parent cleared initialData (e.g. after withdrawing pending request) -> reset form to empty and reload user data
    if (prev != null && (curr == null || curr === undefined)) {
      this.clearForm();
      return;
    }

    if (curr != null) {
      this.applyInitialDataToForm(this.initialData);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadUserData(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      this.userDataFromApi = userData;
      console.log('User data fetched:', userData);
      this.populateFormFromUserData(userData);
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  }

  private applyInitialDataToForm(data: any | null): void {
    if (!data || !this.requestCreditsForm) {
      return;
    }

    // `additionalInfo` payload keys match our form control names in most cases.
    // Patch only known fields; ignore unknown keys.
    const allowedKeys = Object.keys(this.requestCreditsForm.controls);
    const patch: any = {};
    allowedKeys.forEach((k) => {
      if (data[k] !== undefined) {
        patch[k] = data[k];
      }
    });

    // Handle a couple of common backend naming variants
    if (patch['isOrganizationMember'] === undefined) {
      if (data['is_organization_member'] !== undefined) patch['isOrganizationMember'] = data['is_organization_member'];
      if (data['isOrganizationMemberText'] !== undefined && patch['isOrganizationMember'] === undefined) {
        patch['isOrganizationMember'] = String(data['isOrganizationMemberText']).toLowerCase() === 'yes';
      }
    }

    this.requestCreditsForm.patchValue(patch, { emitEvent: false });
    
    // If resumeFile has a value, extract and display the file name
    const resumeFileValue = patch['resumeFile'] || data['resumeFile'];
    if (resumeFileValue && resumeFileValue.trim() !== '') {
      // Try to extract file name from the key (format: "path/to/file-name.pdf")
      // The file name is usually at the end after the last slash
      const fileName = this.extractFileNameFromKey(resumeFileValue);
      if (fileName) {
        this.resumeFileName = fileName;
      }
    }
  }
  
  /**
   * Extract file name from a file key
   * Key format: "path/to/timestamp-uuid-uuid-uuid-filename.pdf" or "timestamp-uuid-uuid-uuid-filename.pdf"
   * The filename is the last segment that contains the file extension
   * Example: "1769053377844-29ca448b-048d-4721-ac2e-5ccd17685130-Forest-Stack Vocab.pdf"
   * Result: "Forest-Stack Vocab.pdf"
   */
  private extractFileNameFromKey(key: string): string | null {
    if (!key) return null;
    
    // If the key contains a slash, get the part after the last slash
    let lastPart = key;
    if (key.includes('/')) {
      const parts = key.split('/');
      lastPart = parts[parts.length - 1];
    }
    
    // The format is typically: "timestamp-uuid-uuid-uuid-filename.pdf"
    // Split by hyphens and find the segment(s) that contain the file extension
    if (lastPart.includes('-')) {
      const segments = lastPart.split('-');
      
      // Find the segment that contains the file extension (.pdf, .doc, .docx)
      let extensionIndex = -1;
      for (let i = segments.length - 1; i >= 0; i--) {
        if (segments[i].match(/\.(pdf|doc|docx)$/i)) {
          extensionIndex = i;
          break;
        }
      }
      
      if (extensionIndex >= 0) {
        // The filename starts from the segment with the extension
        // Check if previous segments are part of the filename (contain spaces or look like text)
        let startIndex = extensionIndex;
        for (let i = extensionIndex - 1; i >= 0; i--) {
          const seg = segments[i];
          // If segment contains spaces or is longer text (not a UUID), it's part of filename
          if (seg.includes(' ') || (seg.length > 15 && !seg.match(/^[0-9a-f-]+$/i))) {
            startIndex = i;
          } else {
            // This looks like a UUID or timestamp, stop here
            break;
          }
        }
        
        // Reconstruct the filename from the identified segments
        return segments.slice(startIndex).join('-');
      }
      
      // If no extension found, return the last part as-is
      return lastPart;
    }
    
    // If no hyphens, return as-is (might already be just the filename)
    return lastPart;
  }

  private disableAllFields(): void {
    if (!this.requestCreditsForm) return;
    Object.keys(this.requestCreditsForm.controls).forEach((key) => {
      this.requestCreditsForm.get(key)?.disable({ emitEvent: false });
    });
  }

  /**
   * Populate form fields from user API response
   */
  private populateFormFromUserData(userData: any): void {
    // Extract the result object if present, otherwise use userData directly
    const result = userData['result'] || userData;
    
    // Populate Applicant Name from API response
    const applicantName = result['name'] || 
                         result['userName'] || 
                         result['fullName'] || 
                         result['displayName'] || 
                         result['firstName'] || 
                         '';
    if (applicantName) {
      const applicantNameControl = this.requestCreditsForm.get('applicantName');
      if (applicantNameControl) {
        applicantNameControl.setValue(applicantName);
        applicantNameControl.disable(); // Keep it disabled
      }
    }

    // Always prefill personal email/location from users API for non-member path.
    const emailFromApi =
      result['email'] ||
      result['emailId'] ||
      result['businessEmail'] ||
      '';
    const locationFromApi =
      result['location'] ||
      result['address'] ||
      result['cityState'] ||
      '';
    this.requestCreditsForm.patchValue(
      {
        emailId: emailFromApi,
        location: locationFromApi,
      },
      { emitEvent: false }
    );

    // Disable any personal fields that were pre-filled from the API.
    if (emailFromApi) {
      this.requestCreditsForm.get('emailId')?.disable({ emitEvent: false });
    }
    if (locationFromApi) {
      this.requestCreditsForm.get('location')?.disable({ emitEvent: false });
    }

    // Extract organisation data
    const organisation = result['organisation'] || result['organization'] || result['org'] || null;
    const hasOrganisationReference = !!(
      organisation ||
      result['organisationId'] ||
      result['organizationId']
    );
    const organisationNameFromApi =
      organisation?.['name'] ||
      organisation?.['organisationName'] ||
      organisation?.['organizationName'] ||
      organisation?.['orgName'] ||
      result['organisationName'] ||
      result['organizationName'] ||
      result['orgName'] ||
      '';
    
    // If user has an organisation reference, set switch to "Yes" and populate available fields
    if (hasOrganisationReference || organisationNameFromApi) {
      // Store organization data for later use
      this.organizationDataFromApi = {
        organisationName: organisationNameFromApi ||
                          organisation?.['name'] || 
                          organisation?.['organisationName'] || 
                          organisation?.['organizationName'] || 
                          organisation?.['orgName'] || 
                          '',
        organisationType: organisation?.['entityType'] || 
                         organisation?.['orgSector'] || 
                         organisation?.['type'] ||
                         result['organisationType'] ||
                         result['organizationType'] ||
                         '',
        organisationLocation: organisation?.['address'] || 
                             organisation?.['location'] ||
                             result['organisationLocation'] ||
                             result['organizationLocation'] ||
                             '',
        organisationWebsiteUrl: organisation?.['websiteLink'] || 
                               organisation?.['website'] || 
                               organisation?.['websiteUrl'] ||
                               result['organisationWebsiteUrl'] ||
                               result['organizationWebsiteUrl'] ||
                               '',
        jobTitle: organisation?.['jobTitle'] || 
                 result['jobTitle'] || 
                 '',
        businessEmail: organisation?.['managerEmail'] || 
                      result['email'] || 
                      organisation?.['email'] || 
                      ''
      };
      
      // Set organization membership to true
      this.requestCreditsForm.patchValue({ isOrganizationMember: true }, { emitEvent: false });
      
      // Patch all organisation fields at once
      this.requestCreditsForm.patchValue(this.organizationDataFromApi, { emitEvent: false });
      
      // Disable only the individual org fields that were actually filled by the API;
      // leave empty fields editable so the user can supply missing information.
      this.disablePopulatedOrganisationFields();
      this.hasNoOrganisation = false;
    } else {
      // No organization data from API
      this.organizationDataFromApi = null;
      this.hasNoOrganisation = true;
      // Disable all organisation fields when user has no organisation
      this.disableOrganisationFields();
    }
  }

  private async refreshOrganisationDataFromUserApi(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      this.userDataFromApi = userData;
      this.populateFormFromUserData(userData);
    } catch (error) {
      console.error('Error refreshing organisation details from user API:', error);
    }
  }

  /**
   * Disable organisation fields after pre-population
   */
  private disableOrganisationFields(): void {
    this.organisationFields.forEach(field => {
      const control = this.requestCreditsForm.get(field);
      if (control) {
        control.disable();
      }
    });
  }

  private enableOrganisationFields(): void {
    this.organisationFields.forEach(field => {
      const control = this.requestCreditsForm.get(field);
      if (control) {
        control.enable();
      }
    });
  }

  /** Disable only the org fields that have an API-supplied value; leave empty ones editable. */
  private disablePopulatedOrganisationFields(): void {
    this.organisationFields.forEach(field => {
      const control = this.requestCreditsForm.get(field);
      if (!control) return;
      const value = this.organizationDataFromApi?.[field];
      const hasValue = value !== null && value !== undefined && String(value).trim() !== '';
      if (hasValue) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  private hasCompleteOrganisationDetails(): boolean {
    if (!this.organizationDataFromApi) {
      return false;
    }
    return this.organisationFields.every((field) => {
      const value = this.organizationDataFromApi[field];
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
  }

  initRequestCreditsForm(): void {
    this.requestCreditsForm = this.fb.group({
      // Applicant Information
      applicantName: [{ value: 'Org Mem20', disabled: true }, [Validators.required]],
      isOrganizationMember: [false, [Validators.required]],
      organisationName: [''],
      organisationType: [''],
      organisationLocation: [''],
      organisationWebsiteUrl: [''],
      jobTitle: [''],
      businessEmail: [''],
      emailId: [''],
      location: [''],
      
      // Project Overview
      projectTitle: ['', [Validators.required]],
      projectDescription: ['', [Validators.required, Validators.maxLength(250)]],
      projectSector: ['', [Validators.required]],
      problemStatement: ['', [Validators.required]],
      tgdexDataset: ['', [Validators.required]],
      endUsers: ['', [Validators.required]],
      projectStage: ['', [Validators.required]],
      
      // Technical Information
      technicalInfo: [''],
      modelType: ['', [Validators.required]],
      trainingType: ['', [Validators.required]],
      inputDataType: ['', [Validators.required]],
      dataVolume: ['', [Validators.required]],
      trainingIterations: [''],
      estimatedBatchSize: [''],
      estimatedGpuHours: ['', [Validators.required]],
      briefJustification: ['', [Validators.required, Validators.maxLength(150)]],
      preferredTimeslots: [''],
      tgdexUsagePreviously: ['', [Validators.required]],
      
      // Team Credentials
      teamCredentials: [''],
      teamSize: ['', [Validators.required]],
      keyMembersRoles: [''],
      pastAiMlExperience: [''],
      projectsPublications: [''],
      resumeFile: [''], // Store file name or reference
      hasSupport: ['', [Validators.required]],
      latestValuation: [''],
      
      // Confirmations
      confirmAccuracy: [false, [Validators.requiredTrue]],
      understandGpuAllocation: [false, [Validators.requiredTrue]]
    });

    // Handle conditional validation based on organization membership
    this.requestCreditsForm.get('isOrganizationMember')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const isMember = value === true;
        
        // Organization fields (required when Yes)
        const orgFields = [...this.organisationFields];
        
        // Personal fields (required when No)
        const personalFields = ['emailId', 'location'];
        
        if (isMember) {
          // Clear validators for personal fields
          personalFields.forEach(field => {
            const control = this.requestCreditsForm.get(field);
            control?.clearValidators();
            control?.setValue('');
            control?.updateValueAndValidity();
          });
          
          // If we have organization data from API, populate and disable filled fields
          if (this.organizationDataFromApi) {
            this.requestCreditsForm.patchValue(this.organizationDataFromApi);
            this.disablePopulatedOrganisationFields();
          } else if (this.hasNoOrganisation) {
            // User has no organisation - keep fields disabled
            this.disableOrganisationFields();
          } else {
            // No API data but user might have organisation (not yet loaded), enable fields so user can enter data
            orgFields.forEach(field => {
              const control = this.requestCreditsForm.get(field);
              control?.enable();
            });
          }
          
          // Add validators for organization fields
          orgFields.forEach(field => {
            const control = this.requestCreditsForm.get(field);
            if (field === 'organisationWebsiteUrl') {
              control?.setValidators([Validators.required, Validators.pattern(/^https?:\/\/.+/)]);
            } else if (field === 'businessEmail') {
              control?.setValidators([Validators.required, Validators.email]);
            } else {
              control?.setValidators([Validators.required]);
            }
            control?.updateValueAndValidity();
          });

          // On explicit "Yes", re-fetch org details from user API and repopulate.
          void this.refreshOrganisationDataFromUserApi();
        } else {
          // Enable organization fields if user switches to "No"
          orgFields.forEach(field => {
            const control = this.requestCreditsForm.get(field);
            control?.enable();
            control?.clearValidators();
            control?.setValue('');
            control?.updateValueAndValidity();
          });
          
          // Add validators for personal fields
          const emailControl = this.requestCreditsForm.get('emailId');
          emailControl?.setValidators([Validators.required, Validators.email]);
          emailControl?.updateValueAndValidity();
          
          const locationControl = this.requestCreditsForm.get('location');
          locationControl?.setValidators([Validators.required]);
          locationControl?.updateValueAndValidity();
        }
      });
  }

  /**
   * Check if all required fields in Project Overview section are filled
   */
  private isProjectOverviewComplete(): boolean {
    const requiredFields = [
      'projectTitle',
      'projectDescription',
      'problemStatement',
      'projectSector',
      'endUsers',
      'tgdexDataset',
      'projectStage'
    ];
    
    return requiredFields.every(field => {
      const control = this.requestCreditsForm.get(field);
      return control && control.valid && control.value !== null && control.value !== '';
    });
  }

  toggleAccordionSection(sectionId: string): void {
    const wasExpanded = this.expandedSections[sectionId];
    
    // If opening a section (not closing), close all other sections first
    if (!wasExpanded) {
      // Close all other sections
      Object.keys(this.expandedSections).forEach(key => {
        if (key !== sectionId) {
          this.expandedSections[key] = false;
        }
      });
      
      // When opening technical-info (step 3), check if project-overview is complete
      if (sectionId === 'technical-info') {
        if (this.isProjectOverviewComplete()) {
          // All required fields filled - project-overview is already closed above
        } else {
          // Required fields not filled - keep project-overview open (but we just closed it, so don't open it)
          // This means if project-overview is incomplete, technical-info won't open
          // Instead, we should show a message or keep project-overview open
          // For now, we'll allow technical-info to open but user should complete project-overview first
        }
      }
      
      // When clicking on the second accordion (Project Overview), capture Applicant Information
      if (sectionId === 'project-overview') {
        this.captureApplicationInformation();
      }
    }
    
    // Toggle the clicked section
    this.expandedSections[sectionId] = !this.expandedSections[sectionId];
  }

  /**
   * Check if all required fields in Applicant Information section are filled and valid
   */
  private isApplicantInfoComplete(): boolean {
    const formValue = this.requestCreditsForm.getRawValue();
    const isOrganizationMember = formValue.isOrganizationMember;
    
    // Check applicant name (required, but disabled so should always have value)
    if (!formValue.applicantName || formValue.applicantName.trim() === '') {
      return false;
    }
    
    // Check if organization membership is selected
    if (isOrganizationMember === null || isOrganizationMember === undefined) {
      return false;
    }
    
    // Get form controls for validation
    const applicantNameControl = this.requestCreditsForm.get('applicantName');
    const isOrganizationMemberControl = this.requestCreditsForm.get('isOrganizationMember');
    
    // Check if basic required fields are valid
    if (applicantNameControl?.invalid || isOrganizationMemberControl?.invalid) {
      return false;
    }
    
    if (isOrganizationMember === true) {
      // Check all organization fields
      const orgFields = [
        'organisationName',
        'organisationType',
        'organisationLocation',
        'organisationWebsiteUrl',
        'jobTitle',
        'businessEmail'
      ];
      
      for (const field of orgFields) {
        const control = this.requestCreditsForm.get(field);
        if (!control || control.invalid || !control.value || control.value.trim() === '') {
          return false;
        }
      }
    } else {
      // Check personal fields
      const emailControl = this.requestCreditsForm.get('emailId');
      const locationControl = this.requestCreditsForm.get('location');
      
      if (!emailControl || emailControl.invalid || !emailControl.value || emailControl.value.trim() === '') {
        return false;
      }
      
      if (!locationControl || locationControl.invalid || !locationControl.value || locationControl.value.trim() === '') {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Capture Applicant Information and log it to console
   */
  private captureApplicationInformation(): void {
    const formValue = this.requestCreditsForm.getRawValue(); // Use getRawValue() to include disabled fields
    
    const isOrganizationMember = formValue.isOrganizationMember || false;
    const switchValue = isOrganizationMember ? 'Yes' : 'No';
    
    const ApplicationInformation: any = {
      applicantName: formValue.applicantName || '',
      isOrganizationMember: isOrganizationMember,
      switchValue: switchValue
    };

    // If user is an organization member, include organization fields
    if (isOrganizationMember === true) {
      ApplicationInformation.organisationName = formValue.organisationName || '';
      ApplicationInformation.organisationType = formValue.organisationType || '';
      ApplicationInformation.organisationLocation = formValue.organisationLocation || '';
      ApplicationInformation.organisationWebsiteUrl = formValue.organisationWebsiteUrl || '';
      ApplicationInformation.jobTitle = formValue.jobTitle || '';
      ApplicationInformation.businessEmail = formValue.businessEmail || '';
    } else {
      // If user is not an organization member, include personal fields
      ApplicationInformation.emailId = formValue.emailId || '';
      ApplicationInformation.location = formValue.location || '';
    }

    console.log('ApplicationInformation:', ApplicationInformation);
  }

  isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections[sectionId] || false;
  }

  /**
   * Check if all required fields in Applicant Information section are complete
   */
  isApplicantInfoSectionComplete(): boolean {
    if (!this.requestCreditsForm) return false;
    
    const formValue = this.requestCreditsForm.getRawValue(); // Use getRawValue to include disabled fields
    const isMember = formValue.isOrganizationMember;
    
    // Check applicant name (required, but may be disabled)
    if (!formValue.applicantName || String(formValue.applicantName).trim() === '') {
      return false;
    }
    
    // Check if organization membership is selected
    if (isMember === null || isMember === undefined) {
      return false;
    }
    
    // If organization member, check organization fields
    if (isMember === true) {
      // If user selected "Yes" but has no organisation data, mark as incomplete
      if (this.hasNoOrganisation) {
        return false;
      }
      
      const orgFields = ['organisationName', 'organisationType', 'organisationLocation', 'jobTitle', 'businessEmail'];
      for (const field of orgFields) {
        const value = formValue[field];
        if (!value || String(value).trim() === '') {
          return false;
        }
      }
    } else {
      // If not organization member, check personal fields
      if (!formValue.emailId || String(formValue.emailId).trim() === '') {
        return false;
      }
      if (!formValue.location || String(formValue.location).trim() === '') {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Check if all required fields in Project Overview section are complete
   */
  isProjectOverviewSectionComplete(): boolean {
    if (!this.requestCreditsForm) return false;
    
    const requiredFields = [
      'projectTitle',
      'projectDescription',
      'projectSector',
      'problemStatement',
      'tgdexDataset',
      'endUsers',
      'projectStage'
    ];

    for (const field of requiredFields) {
      const control = this.requestCreditsForm.get(field);
      if (control?.invalid || !control?.value || String(control.value).trim() === '') {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if all required fields in Technical Information section are complete
   */
  isTechnicalInfoSectionComplete(): boolean {
    if (!this.requestCreditsForm) return false;
    
    const requiredFields = [
      'modelType',
      'trainingType',
      'inputDataType',
      'dataVolume',
      'estimatedGpuHours',
      'briefJustification',
      'tgdexUsagePreviously'
    ];

    for (const field of requiredFields) {
      const control = this.requestCreditsForm.get(field);
      if (control?.invalid || !control?.value || String(control.value).trim() === '') {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if all required fields in Team Credentials section are complete
   */
  isTeamCredentialsSectionComplete(): boolean {
    if (!this.requestCreditsForm) return false;
    
    const requiredFields = [
      'teamSize',
      'hasSupport'
    ];

    for (const field of requiredFields) {
      const control = this.requestCreditsForm.get(field);
      if (control?.invalid || !control?.value || String(control.value).trim() === '') {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if a specific section is complete
   */
  isSectionComplete(sectionId: string): boolean {
    switch (sectionId) {
      case 'applicant-info':
        return this.isApplicantInfoSectionComplete();
      case 'project-overview':
        return this.isProjectOverviewSectionComplete();
      case 'technical-info':
        return this.isTechnicalInfoSectionComplete();
      case 'team-credentials':
        return this.isTeamCredentialsSectionComplete();
      default:
        return false;
    }
  }

  /**
   * Get the completion state of a section: 'none', 'partial', 'complete'
   * In read-only (view) mode, submitted requests have all required fields filled, so show green for all sections.
   */
  getSectionCompletionState(sectionId: string): 'none' | 'partial' | 'complete' {
    if (this.readOnly) return 'complete';
    if (!this.requestCreditsForm) return 'none';
    
    let totalFields = 0;
    let filledFields = 0;
    
    switch (sectionId) {
      case 'applicant-info':
        const formValue = this.requestCreditsForm.getRawValue();
        const isMember = formValue.isOrganizationMember;
        
        // Always required fields
        totalFields = 2; // applicantName, isOrganizationMember
        if (formValue.applicantName && String(formValue.applicantName).trim() !== '') filledFields++;
        if (isMember !== null && isMember !== undefined) filledFields++;
        
        if (isMember === true) {
          // If user selected "Yes" but has no organisation, mark as incomplete (red tick)
          if (this.hasNoOrganisation) {
            // Return 'none' state to show red tick
            return 'none';
          }
          
          const orgFields = ['organisationName', 'organisationType', 'organisationLocation', 'jobTitle', 'businessEmail'];
          totalFields += orgFields.length;
          orgFields.forEach(field => {
            const value = formValue[field];
            if (value && String(value).trim() !== '') filledFields++;
          });
        } else if (isMember === false) {
          totalFields += 2; // emailId, location
          if (formValue.emailId && String(formValue.emailId).trim() !== '') filledFields++;
          if (formValue.location && String(formValue.location).trim() !== '') filledFields++;
        }
        break;
        
      case 'project-overview':
        const projectFields = ['projectTitle', 'projectDescription', 'projectSector', 'problemStatement', 'tgdexDataset', 'endUsers', 'projectStage'];
        totalFields = projectFields.length;
        projectFields.forEach(field => {
          const control = this.requestCreditsForm.get(field);
          const value = control?.value;
          if (value && String(value).trim() !== '') filledFields++;
        });
        break;
        
      case 'technical-info':
        const techFields = ['modelType', 'trainingType', 'inputDataType', 'dataVolume', 'estimatedGpuHours', 'briefJustification', 'tgdexUsagePreviously'];
        totalFields = techFields.length;
        techFields.forEach(field => {
          const control = this.requestCreditsForm.get(field);
          const value = control?.value;
          if (value && String(value).trim() !== '') filledFields++;
        });
        break;
        
      case 'team-credentials':
        const teamFields = ['teamSize', 'hasSupport'];
        totalFields = teamFields.length;
        teamFields.forEach(field => {
          const control = this.requestCreditsForm.get(field);
          const value = control?.value;
          if (value && String(value).trim() !== '') filledFields++;
        });
        break;
    }
    
    if (filledFields === 0) return 'none';
    if (filledFields === totalFields) return 'complete';
    return 'partial';
  }

  /**
   * Get tooltip text for section completion
   */
  getSectionTooltip(sectionId: string): string {
    const state = this.getSectionCompletionState(sectionId);
    if (state === 'complete') {
      return 'All fields are filled';
    }
    return 'You need to fill the fields';
  }

  /**
   * Check if all sections have their required fields filled
   * Returns true only if all sections are complete (no red ticks)
   */
  areAllSectionsComplete(): boolean {
    const sections = ['applicant-info', 'project-overview', 'technical-info', 'team-credentials'];
    
    for (const sectionId of sections) {
      const state = this.getSectionCompletionState(sectionId);
      // If any section has empty required fields (state === 'none'), return false
      if (state === 'none') {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Clear all form values after successful submission
   */
  async clearForm(): Promise<void> {
    if (!this.requestCreditsForm) return;
    
    // Clear file upload
    this.selectedResumeFile = null;
    this.resumeFileName = '';
    this.uploadedResumeUrl = null;
    
    // Clear file input element
    const fileInput = document.getElementById('resumeFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    // Clean up PDF preview blob URL if exists
    if (this.currentPdfUrl && this.currentPdfUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.currentPdfUrl);
    }
    this.currentPdfUrl = null;
    this.showPdfPreviewModal = false;
    
    // Reset accordion states - open only applicant-info
    this.expandedSections = {
      'applicant-info': true,
      'project-overview': false,
      'technical-info': false,
      'team-credentials': false
    };
    
    // Reset the form to initial state
    this.requestCreditsForm.reset();
    
    // Reset organization membership switch to default
    this.requestCreditsForm.patchValue({ isOrganizationMember: false });
    
    // Mark all form controls as untouched to clear validation errors
    Object.keys(this.requestCreditsForm.controls).forEach(key => {
      const control = this.requestCreditsForm.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
    
    // Reload user data to repopulate applicant name and organization data
    if (!this.readOnly) {
      await this.loadUserData();
    }
    
    console.log('Form cleared after successful submission');
  }

  async onResumeFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        console.error('File size exceeds 5MB limit');
        this.toasterService.error('File size exceeds 5MB limit');
        input.value = ''; // Clear the input
        return;
      }
      
      // Check file type
      const allowedTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        console.error('Invalid file type. Only PDF, DOC, and DOCX are allowed.');
        this.toasterService.error('Invalid file type. Only PDF, DOC, and DOCX are allowed.');
        input.value = ''; // Clear the input
        return;
      }
      
      this.selectedResumeFile = file;
      this.resumeFileName = file.name;
      
      // Upload file to API
      try {
        this.pdfLoading = true;
        const uploadResponse = await this.fileApiService.assetUpload(file);
        console.log('File uploaded successfully:', uploadResponse);
        
        // Extract the file key and original name from the response
        // API response structure: { success: true, data: { key: "...", originalname: "...", ... } }
        // uploadResponse structure: { data: { success: true, data: { key: "...", originalname: "...", ... } }, status: number }
        const responseBody = uploadResponse?.data;
        const fileKey = responseBody?.data?.key; // Extract key from response.data.data.key
        const originalFileName = responseBody?.data?.originalname || file.name; // Extract original name or use file.name as fallback
        
        if (fileKey) {
          // Store the file key in the form (this is what we'll send to the API)
          this.requestCreditsForm.patchValue({
            resumeFile: fileKey
          });
          console.log('Resume file key stored:', fileKey);
          
          // Store the original file name for display
          this.resumeFileName = originalFileName;
          
          // Store the key for preview purposes (if needed)
          this.uploadedResumeUrl = fileKey;
        } else {
          console.warn('File uploaded but no key found in response:', uploadResponse);
          // Fallback: store file name if key is not available
          this.requestCreditsForm.patchValue({
            resumeFile: file.name
          });
          this.resumeFileName = file.name;
        }
        
        // If it's a PDF, we can try to show preview (though preview might need a full URL)
        // For now, we'll skip auto-preview since we only have the key, not a full URL
        // The user can still click the PDF icon to preview if needed
        
        this.toasterService.success('File uploaded successfully');
      } catch (error) {
        console.error('Error uploading file:', error);
        this.toasterService.error('Failed to upload file. Please try again.');
        // Clear the file selection on error
        this.selectedResumeFile = null;
        this.resumeFileName = '';
        input.value = '';
      } finally {
        this.pdfLoading = false;
      }
    }
  }
  
  /**
   * Trigger change detection so the PDF viewer renders (avoids empty modal until user clicks).
   */
  private triggerPdfViewerRender(): void {
    this.cdr.detectChanges();
    setTimeout(() => this.cdr.detectChanges(), 150);
  }

  /**
   * Open PDF preview modal
   */
  async openPdfPreview(fileUrl?: string): Promise<void> {
    console.log('openPdfPreview called');
    console.log('selectedResumeFile:', this.selectedResumeFile);
    console.log('resumeFile value:', this.requestCreditsForm?.get('resumeFile')?.value);
    // If we have a local file selected, use it for preview
    if (this.selectedResumeFile && this.selectedResumeFile.type === 'application/pdf') {
      this.pdfLoading = true;
      this.currentPdfUrl = null;
      this.showPdfPreviewModal = true;
      
      try {
        // Create a blob URL from the local file
        const blobUrl = URL.createObjectURL(this.selectedResumeFile);
        this.currentPdfUrl = blobUrl;
        this.pdfLoading = false;
        this.triggerPdfViewerRender();
        return;
      } catch (error) {
        console.error('Error creating blob URL from local file:', error);
        this.toasterService.error('Failed to load PDF preview');
        this.pdfLoading = false;
        this.showPdfPreviewModal = false;
        return;
      }
    }
    
    // Check if we have a resumeFile key in the form (from upload API response)
    const resumeFileKey = this.requestCreditsForm?.get('resumeFile')?.value;
    
    if (resumeFileKey && resumeFileKey.trim() !== '') {
      // We have a file key, call the download API
      this.pdfLoading = true;
      this.currentPdfUrl = null;
      this.showPdfPreviewModal = true;
      
      try {
        console.log('Calling downloadAssets API with key:', resumeFileKey);
        
        // Call the download API with the file key
        const downloadPayload = {
          key: resumeFileKey,
          expiresIn: 0
        };
        
        const downloadResponse = await this.fileApiService.downloadAssets(downloadPayload);
        console.log('Download API response:', downloadResponse);
        
        // Extract the download URL from the response
        // The response structure may vary, so we check multiple possible paths
        const downloadUrl = downloadResponse?.url || 
                           downloadResponse?.data?.url || 
                           downloadResponse?.result?.url ||
                           downloadResponse?.downloadUrl;
        
        if (downloadUrl) {
          // Fetch the file as blob to create a preview URL
          try {
            const response = await fetch(downloadUrl);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            this.currentPdfUrl = blobUrl;
          } catch (fetchError) {
            console.warn('Error fetching PDF as blob, using direct URL:', fetchError);
            // Fallback: use the URL directly
            this.currentPdfUrl = downloadUrl;
          }
        } else {
          // If the response contains blob data directly
          if (downloadResponse?.data instanceof Blob) {
            const blobUrl = URL.createObjectURL(downloadResponse.data);
            this.currentPdfUrl = blobUrl;
          } else {
            throw new Error('No download URL or blob data found in response');
          }
        }
      } catch (error) {
        console.error('Error downloading file for preview:', error);
        this.toasterService.error('Failed to load PDF preview');
        this.showPdfPreviewModal = false;
      } finally {
        this.pdfLoading = false;
        if (this.currentPdfUrl) {
          this.triggerPdfViewerRender();
        }
      }
      return;
    }
    
    // Fallback: use the uploaded URL if available
    const urlToUse = fileUrl || this.uploadedResumeUrl;
    if (!urlToUse) {
      console.error('No file URL or key available for preview');
      this.toasterService.error('No file available for preview');
      return;
    }
    
    this.pdfLoading = true;
    this.currentPdfUrl = null;
    this.showPdfPreviewModal = true;
    
    try {
      // If it's a direct URL, try to fetch as blob to avoid X-Frame-Options issues
      if (urlToUse.startsWith('http://') || urlToUse.startsWith('https://')) {
        try {
          const response = await fetch(urlToUse);
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          this.currentPdfUrl = blobUrl;
        } catch (fetchError) {
          console.warn('Error fetching PDF as blob, using direct URL:', fetchError);
          // Fallback: use the URL directly
          this.currentPdfUrl = urlToUse;
        }
      } else {
        this.currentPdfUrl = urlToUse;
      }
    } catch (error) {
      console.error('Error opening PDF preview:', error);
      this.toasterService.error('Failed to load PDF preview');
    } finally {
      this.pdfLoading = false;
      if (this.currentPdfUrl) {
        this.triggerPdfViewerRender();
      }
    }
  }
  
  /**
   * Close PDF preview modal
   */
  closePdfPreview(): void {
    this.showPdfPreviewModal = false;
    // Clean up blob URL if it was created
    if (this.currentPdfUrl && this.currentPdfUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.currentPdfUrl);
    }
    this.currentPdfUrl = null;
    this.pdfLoading = false;
  }

  /**
   * Select support option (Yes/No)
   */
  selectSupportOption(value: string): void {
    this.requestCreditsForm.patchValue({
      hasSupport: value
    });
    this.requestCreditsForm.get('hasSupport')?.markAsTouched();
  }

  /**
   * Select training type option
   */
  selectTrainingType(value: string): void {
    this.requestCreditsForm.patchValue({
      trainingType: value
    });
    this.requestCreditsForm.get('trainingType')?.markAsTouched();
  }

  /**
   */
  selectTgdexUsage(value: string): void {
    this.requestCreditsForm.patchValue({
      tgdexUsagePreviously: value
    });
    this.requestCreditsForm.get('tgdexUsagePreviously')?.markAsTouched();
  }

  removeResumeFile(): void {
    this.selectedResumeFile = null;
    this.resumeFileName = '';
    this.uploadedResumeUrl = null;
    this.requestCreditsForm.patchValue({
      resumeFile: ''
    });
    
    // Clean up blob URL if it exists
    if (this.currentPdfUrl && this.currentPdfUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.currentPdfUrl);
    }
    this.currentPdfUrl = null;
    
    // Clear the file input
    const fileInput = document.getElementById('resumeFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  async requestCredits(): Promise<void> {
    console.log('Submit button clicked - requestCredits() called');
    
    // Mark all form controls as touched to show validation errors
    Object.keys(this.requestCreditsForm.controls).forEach(key => {
      const control = this.requestCreditsForm.get(key);
      control?.markAsTouched();
    });
    
    // Check if form is valid
    if (!this.requestCreditsForm.valid) {
      console.warn('Form is invalid, cannot submit');
      console.log('Form valid:', this.requestCreditsForm.valid);
      console.log('Form errors:', this.requestCreditsForm.errors);
      
      // Log all invalid fields
      const invalidFields: string[] = [];
      Object.keys(this.requestCreditsForm.controls).forEach(key => {
        const control = this.requestCreditsForm.get(key);
        if (control?.invalid) {
          invalidFields.push(key);
          console.log(`Field ${key} is invalid:`, control.errors);
        }
      });
      console.log('Invalid fields:', invalidFields);
      
      // Show error message
      this.toasterService.error('Please fill all required fields before submitting');
      return;
    }
    
    console.log('Form valid:', this.requestCreditsForm.valid);
    console.log('Form errors:', this.requestCreditsForm.errors);
    
    // Get all form values as an object (regardless of validity)
    const formData = this.requestCreditsForm.getRawValue();
    console.log('Raw form data:', formData);
    
    // Get switch value explicitly
    const switchValue = formData.isOrganizationMember;
    const switchValueText = switchValue ? 'Yes' : 'No';
    console.log('Switch value (isOrganizationMember):', switchValue);
    console.log('Switch value (text):', switchValueText);
    
    // Create a comprehensive object with all field values
    const allFormValues: { [key: string]: any } = {
      // Applicant Information
      applicantName: formData.applicantName || null,
      isOrganizationMember: switchValue || false,
      isOrganizationMemberText: switchValueText, // Add text representation
      
      // Organization fields (if isOrganizationMember is true)
      ...(formData.isOrganizationMember ? {
        organisationName: formData.organisationName || null,
        organisationType: formData.organisationType || null,
        organisationLocation: formData.organisationLocation || null,
        organisationWebsiteUrl: formData.organisationWebsiteUrl || null,
        jobTitle: formData.jobTitle || null,
        businessEmail: formData.businessEmail || null
      } : {
        // Personal fields (if isOrganizationMember is false)
        emailId: formData.emailId || null,
        location: formData.location || null
      }),
      
      // Project Overview
      projectTitle: formData.projectTitle || null,
      projectDescription: formData.projectDescription || null,
      problemStatement: formData.problemStatement || null,
      projectSector: formData.projectSector || null,
      endUsers: formData.endUsers || null,
      tgdexDataset: formData.tgdexDataset || null,
      projectStage: formData.projectStage || null,
      
      // Technical Information
      technicalInfo: formData.technicalInfo || null,
      modelType: formData.modelType || null,
      trainingType: formData.trainingType || null,
      inputDataType: formData.inputDataType || null,
      dataVolume: formData.dataVolume || null,
      trainingIterations: formData.trainingIterations || null,
      estimatedBatchSize: formData.estimatedBatchSize || null,
      estimatedGpuHours: formData.estimatedGpuHours || null,
      briefJustification: formData.briefJustification || null,
      preferredTimeslots: formData.preferredTimeslots || null,
      tgdexUsagePreviously: formData.tgdexUsagePreviously || null,
      
      // Team Credentials
      teamCredentials: formData.teamCredentials || null,
      teamSize: formData.teamSize || null,
      keyMembersRoles: formData.keyMembersRoles || null,
      pastAiMlExperience: formData.pastAiMlExperience || null,
      projectsPublications: formData.projectsPublications || null,
      resumeFile: formData.resumeFile || null, // Use the stored file key from upload response (data.key)
      hasSupport: formData.hasSupport || null,
      latestValuation: formData.latestValuation || null,
      
      // Confirmations
      confirmAccuracy: formData.confirmAccuracy || false,
      understandGpuAllocation: formData.understandGpuAllocation || false
    };
    
    console.log('All form values as object:', allFormValues);
    console.log('Form values (key-value pairs):', JSON.stringify(allFormValues, null, 2));
    
    // Log detailed validation status
    const invalidFields: string[] = [];
    Object.keys(this.requestCreditsForm.controls).forEach(key => {
      const control = this.requestCreditsForm.get(key);
      if (control?.invalid) {
        invalidFields.push(key);
        control.markAsTouched();
        console.log(`Field ${key} is invalid:`, control.errors);
      }
    });
    
    console.log('Invalid fields:', invalidFields);
    console.log('Form valid status:', this.requestCreditsForm.valid);
    console.log('Form status:', this.requestCreditsForm.status);
    
    // Call API regardless of validation (or you can keep the validation check)
    // For now, let's call it even if invalid to see if it works, but log a warning
    if (!this.requestCreditsForm.valid) {
      console.warn('Form has validation errors, but proceeding with API call anyway');
      console.warn('Invalid fields:', invalidFields);
    }
    
    try {
      // Wrap all form values under additional_info as required by API
      const requestPayload = {
        additional_info: allFormValues,
      };

      console.log('Calling credit request API with payload:', requestPayload);
      const response = await this.authApiService.getCreditRequestPost(requestPayload);
      console.log('Credit request API response received:', response);
      
      // If we reach here, the request was successful (200 OK)
      // Show green success toaster
      this.toasterService.success("You're request has been sent successfully");
      console.log('Request sent successfully - 200 OK');
      
      // Notify parent so it can refresh pending state and show the pending warning div with withdraw button
      this.requestSubmitted.emit();
      
      // Clear all form values after successful submission
      this.clearForm();
    } catch (error: any) {
      console.error('Error calling computeRequest API:', error);
      
      // Check if error is a 409 Conflict
      // Error structure can vary: error.status, error.error.status, or error.response?.status
      const statusCode = error?.status || error?.error?.status || error?.response?.status;
      
      if (statusCode === 409) {
        // Extract detail message from error response
        // Try multiple possible locations for the detail message
        const detailMessage = 
          error?.error?.detail || 
          error?.detail || 
          error?.response?.data?.detail ||
          error?.response?.detail ||
          error?.message || 
          'Entry already exists';
        
        // Show red error toaster with detail message
        this.toasterService.error(detailMessage);
        console.error('409 Conflict error - detail:', detailMessage);
        console.error('Full error object:', error);
      } else {
        // For other errors, let NetworkService handle it (toaster will be shown automatically)
        console.error('Other error occurred:', error);
      }
    }
  }

  /**
   * Fill form from credit request history data (called when user clicks "Fill" in history modal).
   * Populates all fields from the additionalInfo object.
   */
  fillFormFromHistory(additionalInfo: any): void {
    if (!additionalInfo || !this.requestCreditsForm) {
      return;
    }

    // Map additionalInfo fields to form controls and populate
    const formData: any = {};
    
    // Project Overview
    if (additionalInfo.projectTitle !== undefined && additionalInfo.projectTitle !== null) formData.projectTitle = additionalInfo.projectTitle;
    if (additionalInfo.projectDescription !== undefined && additionalInfo.projectDescription !== null) formData.projectDescription = additionalInfo.projectDescription;
    if (additionalInfo.problemStatement !== undefined && additionalInfo.problemStatement !== null) formData.problemStatement = additionalInfo.problemStatement;
    if (additionalInfo.projectSector !== undefined && additionalInfo.projectSector !== null) formData.projectSector = additionalInfo.projectSector;
    if (additionalInfo.endUsers !== undefined && additionalInfo.endUsers !== null) formData.endUsers = additionalInfo.endUsers;
    if (additionalInfo.tgdexDataset !== undefined && additionalInfo.tgdexDataset !== null) formData.tgdexDataset = additionalInfo.tgdexDataset;
    if (additionalInfo.projectStage !== undefined && additionalInfo.projectStage !== null) formData.projectStage = additionalInfo.projectStage;
    
    // Technical Information
    if (additionalInfo.technicalInfo !== undefined && additionalInfo.technicalInfo !== null) formData.technicalInfo = additionalInfo.technicalInfo;
    if (additionalInfo.modelType !== undefined && additionalInfo.modelType !== null) formData.modelType = additionalInfo.modelType;
    if (additionalInfo.trainingType !== undefined && additionalInfo.trainingType !== null) formData.trainingType = additionalInfo.trainingType;
    if (additionalInfo.inputDataType !== undefined && additionalInfo.inputDataType !== null) formData.inputDataType = additionalInfo.inputDataType;
    if (additionalInfo.dataVolume !== undefined && additionalInfo.dataVolume !== null) formData.dataVolume = additionalInfo.dataVolume;
    if (additionalInfo.trainingIterations !== undefined && additionalInfo.trainingIterations !== null) formData.trainingIterations = additionalInfo.trainingIterations;
    if (additionalInfo.estimatedBatchSize !== undefined && additionalInfo.estimatedBatchSize !== null) formData.estimatedBatchSize = additionalInfo.estimatedBatchSize;
    if (additionalInfo.estimatedGpuHours !== undefined && additionalInfo.estimatedGpuHours !== null) formData.estimatedGpuHours = additionalInfo.estimatedGpuHours;
    if (additionalInfo.briefJustification !== undefined && additionalInfo.briefJustification !== null) formData.briefJustification = additionalInfo.briefJustification;
    if (additionalInfo.preferredTimeslots !== undefined && additionalInfo.preferredTimeslots !== null) formData.preferredTimeslots = additionalInfo.preferredTimeslots;
    if (additionalInfo.tgdexUsagePreviously !== undefined && additionalInfo.tgdexUsagePreviously !== null) formData.tgdexUsagePreviously = additionalInfo.tgdexUsagePreviously;
    
    // Team & Credentials
    if (additionalInfo.teamCredentials !== undefined && additionalInfo.teamCredentials !== null) formData.teamCredentials = additionalInfo.teamCredentials;
    if (additionalInfo.teamSize !== undefined && additionalInfo.teamSize !== null) formData.teamSize = additionalInfo.teamSize;
    if (additionalInfo.keyMembersRoles !== undefined && additionalInfo.keyMembersRoles !== null) formData.keyMembersRoles = additionalInfo.keyMembersRoles;
    if (additionalInfo.pastAiMlExperience !== undefined && additionalInfo.pastAiMlExperience !== null) formData.pastAiMlExperience = additionalInfo.pastAiMlExperience;
    if (additionalInfo.projectsPublications !== undefined && additionalInfo.projectsPublications !== null) formData.projectsPublications = additionalInfo.projectsPublications;
    if (additionalInfo.resumeFile !== undefined && additionalInfo.resumeFile !== null) {
      formData.resumeFile = additionalInfo.resumeFile;
      this.uploadedResumeUrl = additionalInfo.resumeFile;
    }
    if (additionalInfo.hasSupport !== undefined && additionalInfo.hasSupport !== null) formData.hasSupport = additionalInfo.hasSupport;
    if (additionalInfo.latestValuation !== undefined && additionalInfo.latestValuation !== null) formData.latestValuation = additionalInfo.latestValuation;
    
    // Confirmations (don't auto-fill these - user should check them again)
    // if (additionalInfo.confirmAccuracy !== undefined) formData.confirmAccuracy = additionalInfo.confirmAccuracy;
    // if (additionalInfo.understandGpuAllocation !== undefined) formData.understandGpuAllocation = additionalInfo.understandGpuAllocation;
    
    // Patch form with all fields
    this.requestCreditsForm.patchValue(formData);
    
    // Mark all fields as touched to show them as filled
    Object.keys(formData).forEach(key => {
      this.requestCreditsForm.get(key)?.markAsTouched();
    });
    
    // Expand all sections so user can see the filled data
    this.expandedSections = {
      'applicant-info': true,
      'project-overview': true,
      'technical-info': true,
      'team-credentials': true
    };
    
    // Trigger change detection
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    console.log('Form filled from history with data:', formData);
    console.log('AdditionalInfo received:', additionalInfo);
  }
}
